'use client'

import { useEffect, useState } from 'react'

export interface UTMParams {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  utm_id?: string
  ref?: string
  company?: string
  email?: string
  fbclid?: string // Facebook Click ID for Meta ads
  gclid?: string // Google Click ID
  msclkid?: string // Microsoft Click ID
  [key: string]: string | undefined
}

interface PersistedParams extends UTMParams {
  landingPage?: string
  timestamp?: string
}

export const useUTMPersistence = () => {
  const [persistedParams, setPersistedParams] = useState<PersistedParams>({})
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    // Mark as client-side to prevent hydration mismatch
    setIsClient(true)
  }, [])

  useEffect(() => {
    // Only run on client side after hydration
    if (!isClient || typeof window === 'undefined') return

    // Function to get URL parameters
    const getUrlParams = (): UTMParams => {
      const params: UTMParams = {}
      const urlSearchParams = new URLSearchParams(window.location.search)

      // Extract all URL parameters
      urlSearchParams.forEach((value, key) => {
        params[key] = decodeURIComponent(value)
      })

      return params
    }

    // Function to check if any tracking parameters are present
    const hasTrackingParams = (params: UTMParams): boolean => {
      const trackingKeys = [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
        'utm_id',
        'ref',
        'company',
        'email',
        'gclid',
        'fbclid',
        'msclkid',
      ]
      return trackingKeys.some((key) => params[key])
    }

    // Function to persist parameters to sessionStorage
    const persistParams = (params: PersistedParams) => {
      try {
        sessionStorage.setItem('trackingParams', JSON.stringify(params))
      } catch (error) {
        console.warn('Could not persist tracking parameters:', error)
      }
    }

    // Function to get persisted parameters from sessionStorage
    const getPersistedParams = (): PersistedParams => {
      try {
        const stored = sessionStorage.getItem('trackingParams')
        return stored ? JSON.parse(stored) : {}
      } catch (error) {
        console.warn('Could not retrieve persisted tracking parameters:', error)
        return {}
      }
    }

    // Function to update URL with parameters without page refresh
    const updateUrlWithParams = (params: UTMParams) => {
      try {
        const url = new URL(window.location.href)

        // Add/update parameters
        Object.entries(params).forEach(([key, value]) => {
          if (value) {
            url.searchParams.set(key, value)
          }
        })

        // Update URL without page refresh - use timeout to prevent hydration issues
        setTimeout(() => {
          window.history.replaceState({ path: url.href }, '', url.href)
        }, 100)
      } catch (error) {
        console.warn('Could not update URL with parameters:', error)
      }
    }

    // Main logic
    const currentParams = getUrlParams()

    if (hasTrackingParams(currentParams)) {
      // New tracking parameters found - store them
      const paramsWithMetadata: PersistedParams = {
        ...currentParams,
        landingPage: window.location.pathname,
        timestamp: new Date().toISOString(),
      }

      persistParams(paramsWithMetadata)
      setPersistedParams(paramsWithMetadata)
    } else {
      // No tracking parameters in current URL - check if we have stored ones
      const storedParams = getPersistedParams()

      if (Object.keys(storedParams).length > 0) {
        // Apply stored parameters to current URL
        const paramsToApply = { ...storedParams }
        // Remove metadata before applying to URL
        delete paramsToApply.landingPage
        delete paramsToApply.timestamp

        updateUrlWithParams(paramsToApply)
        setPersistedParams(storedParams)
      }
    }
  }, [isClient])

  // Function to clear persisted parameters
  const clearPersistedParams = () => {
    if (typeof window === 'undefined') return

    try {
      sessionStorage.removeItem('trackingParams')
      setPersistedParams({})
    } catch (error) {
      console.warn('Could not clear persisted tracking parameters:', error)
    }
  }

  // Function to get parameters for form submission
  const getParamsForSubmission = (): PersistedParams => {
    return persistedParams
  }

  return {
    persistedParams,
    clearPersistedParams,
    getParamsForSubmission,
  }
}
